/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import React, { useState } from 'react';
import InputBase from '@mui/material/InputBase';
import type { InputProps } from '@mui/material';
import type {
  IInputBaseProps,
  InputPropsToExcludeFromMuiProps,
} from './InputControlWrapper';
import InputControlWrapper from './InputControlWrapper';
import { ELEMENT_ID_DATA_TEST_ATTRIBUTE } from '../../../constants/data-element-tracking-ids';

// This deliberately does not include 'number'. By default,
// a scroll wheel input over a 'number' field increments or decrements
// it, which happens a lot accidentally and rarely happens on purpose.
// Users typically interpret this behavior as a bug.
// Use 'tel' type input if you want to bring up a numeric keyboard on
// mobile without causing this scrollwheel issue.
// See https://stackoverflow.com/questions/9712295/disable-scrolling-on-input-type-number
// for some discussion of this
export type TextboxInputType =
  | 'date'
  | 'email'
  | 'hidden'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'url';

export interface IInputProps extends IInputBaseProps<string> {
  type: TextboxInputType;
  maxChar?: number;
}

const Input: React.FC<
Omit<InputProps, InputPropsToExcludeFromMuiProps | 'type'> & IInputProps
> = (inputProps) => {
  const {
    id,
    defaultValue,
    value,
    onChange,
    placeholderText,
    helperText,
    customError,
    startAdornmentElement,
    endAdornmentElement,
    theme,
    maxChar,
    additionalStyles,
    dataElementId,
    ...props
  } = inputProps;
  const [inputValue, setInputValue] = useState(defaultValue || '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    } else {
      setInputValue(e.target?.value);
    }
  };

  return (
    <InputControlWrapper
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...inputProps}
      dataElementId={dataElementId}
      renderInput={({
        renderStartAdornment,
        renderEndAdornment,
        inputStyles,
        id: controlId,
      }) => (
        <InputBase
          id={controlId}
          sx={inputStyles(undefined)}
          defaultValue={defaultValue}
          startAdornment={renderStartAdornment(startAdornmentElement)}
          endAdornment={renderEndAdornment(endAdornmentElement)}
          onChange={handleChange}
          value={value || inputValue}
          placeholder={placeholderText}
          inputProps={{
            ...(maxChar ? { maxlength: maxChar } : {}),
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-input` }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    />
  );
};

export default Input;
